import React, {useState, useEffect, useMemo, forwardRef, ForwardedRef}
	from "react"
import {useNavigate}
	from "react-router-dom"
import {FontAwesomeIcon}
	from "@fortawesome/react-fontawesome"
import {solid, regular}
	from "@fortawesome/fontawesome-svg-core/import.macro"
import {useDispatch, useSelector}
	from "react-redux"
import _
	from "lodash"
import {sprintf}
	from "sprintf-js"
import {Popover}
	from "react-tiny-popover"

import
{
	RootState,
	enableIdleLockSwitch, disableIdleLockSwitch,
	displayNotice
}
	from "./../../globals"
import EnrollmentPrompt
	from "./../management/enrollments/enrollmentPrompt"
import {DataDisplayModal, UserPrompt}
	from "../../components/sectionContainer"
import CalendarSideFilter, {unrestrictedResultsDisplay}
	from "./calendarSideFilter"
import {useEsTelefono}
	from "../../utils/esTelefono"
import LeDatePicker
	from "./../../components/leDatePicker"
import CustomIcon
	from "./../../components/customIcon"
import ClassroomPrompt
	from "../../components/classroomPrompt"
import
{
	baseRole,
	weekDays,
	monthRef,
	retardWeekDayOffset,
	count,
	attendanceStatus,
	isEmpty,
	enrollingSet
}
	from "./../../common"
import HttpManager
	from "./../../services/HttpManager"
import CoursesService
	from "./../../services/campus/courses.service"
import PeriodsService
	from "./../../services/campus/periods.service"
import BuildingsService
	from "./../../services/campus/buildings.service"
import CalendarService
	from "./../../services/campus/calendar.service"
import {cursoCorto}
	from "./../../utils/cursoCorto"
import {horarioBonito}
	from "./../../utils/horarioBonito"
import {nombreDeProfesor, nombreDeAlumno}
	from "./../../utils/nombreCorto"
import EmployeesService
	from "./../../services/campus/employees.service"
import AttendancesService
	from "./../../services/campus/attendances.service"
import CatalogService
	from "./../../services/catalog.service"
import Constants,
{
	employeeStatus,
	ENROLLMENT_STATUS,
	scheduleStatus
}
	from "./../../constants"
import {useCapacityStyles, usePendingBalancesStyles, useEnrollmentStyles, useAttendanceStyles}
	from "./../../hooks/useDailyViewStyles"
import staticData
	from "./../../assets/js/static.json"
import courseImage
	from "./../../assets/images/course.png"

import "./../../components/listaTarjetas/listaTarjetas.css"
import "./../../components/dropdown/style.css"
import { ahoraCdmx } from "../../utils/horaMexico"

const CapacityComponent = (props : any)=>
{
	const {currentCapacity, maxCapacity} = props

	return <span style={useCapacityStyles(currentCapacity, maxCapacity)}>
		{currentCapacity}/{maxCapacity}
	</span>
}

const EnrollmentsComponent = (props : any)=>
{
	const {currentEnrollments, minEnrollments} = props

	return <span style={useEnrollmentStyles(currentEnrollments, minEnrollments)}>
		{currentEnrollments}/{minEnrollments}
	</span>
}

/*
	Presentes de hoy sobre los que deberian venir. (Esteban, 2026-09-21)

	Antes decia "0/0" en una clase con doce matriculados: el denominador era
	cuantas asistencias se habian guardado y el numerador leia una columna que no
	existe en la base, asi que los presentes daban cero siempre.

	Ahora, si nadie paso lista se dice con todas sus letras en vez de enseñar un
	cero que se confunde con "no vino nadie".
*/
const AttendanceComponent = (props : any)=>
{
	const {currentAttendance, idealAttendance, listTaken} = props
	//El estilo se calcula siempre: es un hook y no puede ir detras de un return.
	const estilo = useAttendanceStyles(currentAttendance, idealAttendance)

	return listTaken === false
			?
		<span style={{color : "var(--wa-tenue)", fontWeight : 700}}>
			Sin pasar
			{!idealAttendance ? "" : " · " + idealAttendance + " matriculados"}
		</span>
			:
		<span style={estilo}>
			{currentAttendance}/{idealAttendance}
		</span>
}

const PendingBalanceComponent = (props : any)=>
{
	const {pendingBalancesCount} = props

	return <span style={usePendingBalancesStyles(pendingBalancesCount)}>
		{pendingBalancesCount}
	</span>
}

/*
	Las dos etiquetas de abajo de cada horario: edificio y aula.

	Estaban escritas dos veces con estilos en linea, no partian renglon y llevaban
	un verde neon (#39FF45) que no es de la marca. Con un nombre largo -- "Aula 12
	ANTERIOR" -- el texto se salia del recuadro. Ahora parten renglon, se achican
	si no caben y usan el verde de la marca. (Esteban, 2026-09-20)
*/
const ChipAula = ({texto, tono} : {texto : React.ReactNode, tono : "edificio" | "aula"}) =>
	<span className="calDiaChip">
		<span className={tono === "aula" ? "calDiaChipFilo aula" : "calDiaChipFilo"} />
		<span className="calDiaChipTexto">
			{texto}
		</span>
	</span>

const DailyView = ()=>
{
	const COURSES : number = 0
	const MENTORS : number = 1
	const PERIODS : number = 2
	const BUILDINGS : number = 3
	const ENDING : number = 4

	const navegar = useNavigate()

	const DAY_HALF_HOURS   = 30

	const [courses, setCourses] = useState<any[]>([])
	const [periods, setPeriods] = useState<any[]>([])
	const [mentors, setMentors] = useState<any[]>([])
	const [courseRef, setCourseRef] = useState<any>({})
	const [levelRef, setLevelRef] = useState<any>({})
	const [levels, setLevels] = useState<any>({})
	const [classrooms, setClassrooms] = useState<any>({})
	/*
		En telefono las tres jornadas no caben una al lado de otra: la tabla pide
		1 500px y el panel de filtros otros 344. Se muestra una jornada a la vez y
		los filtros pasan a un panel que se abre. (Esteban, 2026-09-16)
	*/
	const esTelefono : boolean = useEsTelefono(1024)
	const [filtrosAbiertos, setFiltrosAbiertos] = useState<boolean>(false)
	const [distributionRef, setDistributionRef] = useState<any>({})
	/*
		En que media hora arranca cada clase. Se guarda al armar la distribucion
		porque el horario no trae la hora como numero (el nombre la lleva como
		texto), y las columnas de jornada necesitan ordenar las clases de varias
		aulas en una sola lista. (Esteban, 2026-09-20)
	*/
	const [ordenRef, setOrdenRef] = useState<any>({})
	const [periodRef, setPeriodRef] = useState<any>({})
	const [spaceRef, setSpaceRef] = useState<any>({})
	const [currentDay, setCurrentDay] = useState<any>(null)
	const [currentWeekDay, setCurrentWeekDay] = useState<any>(null)
	const dispatch = useDispatch()
	const [initSetup, setInitSetup] = useState<number | null>(null)
	const [noAttendancesFoundOn, setNoAttendancesFoundOn] = useState<string | null>(null)
	const [currentDayCountInPeriod, setCurrentDayCountInPeriod] = useState<number>(0)
	const [totalRecords, setTotalRecords] = useState<number>(0)
	const [data, setData] = useState<any[]>([])
	const [buildings, setBuildings] = useState<any[]>([])
	const [yearsRange, setYearsRange] = useState<any>([])
	const [minDate, setMinDate] = useState<any>([])
	const [maxDate, setMaxDate] = useState<any>([])
	const [buildingRef, setBuildingRef] = useState<any>({})
	const [classroomRef, setClassroomRef] = useState<any>({})
	const [dayAttendanceRef, setDayAttendanceRef] = useState<any>({})
	const icons             : any = [solid("chevron-left"), solid("chevron-right")]
	const idleLocked        : boolean = useSelector((state : RootState) => state.idleLockSwitch.value)
	const sessionToken      : string | null = useSelector((state : RootState) => state.sessionToken.value)
	const themeToggle       : boolean = useSelector((state : RootState) => state.themeToggle.value)
	const currentUserCampus : any = useSelector((state : RootState) => state.currentUserCampus.value);
	const campusRef         : any = useSelector((state : RootState) => state.campusRef.value)
	const basicInfo         : any = useSelector((state : RootState) => state.basicInfo.value)
	const [selectedUser, setSelectedUser] = useState<any>(null)
	const [selectedEnrollment, setSelectedEnrollment] = useState<any>(null)
	const [selectedClassroom, setSelectedClassroom] = useState<any>(null)
	const [today, setToday] = useState<any>(null)
	const [userCampus, setUserCampus] = useState<number>(currentUserCampus)
	/*
		Abria siempre en la primera jornada, aunque fueran las seis de la tarde: el
		maestro tenia que buscar la suya cada vez que entraba. Ahora arranca en la
		que esta corriendo. Solo al abrir; despues manda quien navega.
		(Esteban, 2026-09-16)

		El plantel guarda el inicio de cada jornada como hora * 10 + minutos / 6,
		asi que la hora actual se lleva a ese mismo codigo para poder compararlas.
		Antes de la primera y despues de la ultima cae en la de la punta, que es lo
		que alguien esperaria ver a las seis de la manana o a las once de la noche.
	*/
	const [jornadaVisible, setJornadaVisible] = useState<number>
	(
		()=>
		{
			const plantel = campusRef[currentUserCampus]

			if(plantel == null) return 0

			const ahora  = ahoraCdmx()
			const codigo = (ahora.getHours() * 10) + Math.floor(ahora.getMinutes() / 6)

			if(codigo >= plantel.evening_shift_start)   return 2
			if(codigo >= plantel.afternoon_shift_start) return 1

			return 0
		}
	)
	const [currencySymbol, setCurrencySymbol] = useState<string>(basicInfo.currencyRef["C_" + campusRef[currentUserCampus].company.currency_id].symbol)
	const [currencyCode, setCurrencyCode] = useState<string>(basicInfo.currencyRef["C_" + campusRef[currentUserCampus].company.currency_id].code)
	const [sectionLoaded, setSectionLoaded] = useState<boolean>(false)
	const [notToday, setNotToday] = useState<boolean>(false)
	const [notADayBefore, setNotADayBefore] = useState<boolean>(false)
	const [notADayAfter, setNotADayAfter] = useState<boolean>(false)
	const [clearSearchRequested, setClearSearchRequested] = useState<boolean>(false)
	const [noResultsFound, setNoResultsFound] = useState<boolean>(false)
	const [unrestrictedDisplay, setUnrestrictedDisplay] = useState<boolean>(true)
	// Espacios libres OCULTOS por defecto en la vista diaria (Esteban, 2026-09-11);
	// el ojo del panel de filtros los muestra cuando hacen falta.
	const [availabilityDisplay, setAvailabilityDisplay] = useState<boolean>(false)
	const [selectedSchedule, setSelectedSchedule] = useState<any>(null)
	const [compensationStudentRef, setCompensationStudentRef] = useState<any>(null)
	const [compensationDayStudentRef, setCompensationDayStudentRef] = useState<any>(null)
	const [dateRef, setDateRef] = useState<any>(null)
	const [attendanceStudentRef, setAttendanceStudentRef] = useState<any>(null)
	const [dateCount, setDateCount] = useState<any>(null)
	const [attendanceCountRef, setAttendanceCountRef] = useState<any>(null)
	const [dateList, setDateList] = useState<any>(null)
	const [monthDateCount, setMonthDateCount] = useState<any>(null)
	/*
		Los filtros del calendario diario. (Esteban, 2026-09-21)

		Periodos, profesores, cursos, edificios y aulas son de VARIOS, y se aplican
		aqui mismo sobre lo que ya vino del servidor: el dia entero ya esta cargado,
		asi que filtrar no vuelve a pedir nada y la pantalla responde al instante.

		Se fueron "Capacidad" e "Ins/Min", que nadie usaba, y "Asistencias" dejo de
		ser un rango de numeros para ser lo que de verdad se pregunta: quien ya paso
		lista y quien no la ha marcado.
	*/
	const defaultSearch : any=
	{
		q : "",
		period : null,
		periods : [],
		buildings : [],
		classrooms : [],
		courses : [], 
		mentors : [],
		//"todas" | "hechas" | "faltan"
		attendanceState : "todas",
		onlyPendingTuitions : false,
		onlyPendingKits : false
	}

	const [customSearch, setCustomSearch]       = useState<any>(defaultSearch)
	const [currentSearch, setCurrentSearch]     = useState<any>(defaultSearch)
	const [dayDistribution, setDayDistribution] = useState<any>([])
	const [scheduleRef, setScheduleRef]         = useState<any>([])
	var   errorMessage  : string

	const events = [{title : "Meeting", start : new Date()}]

	let eventGuid = 0;
	const createEventId = () => String(eventGuid++);

	const handleSearchChange = (e : any)=>
	{
		if(e && e.target)
		{
			/*
				"period" (uno) se conserva porque de el sale el aviso de "ese dia esta
				fuera del periodo". Cuando se eligen varios, se queda con el primero.
			*/
			const extra : any = e.target.name === "periods"
				? {period : (e.target.value || [])[0] || null}
				: {}

			setCustomSearch({...customSearch, [e.target.name] : e.target.value, ...extra})
		}
	}

	/*
		Ya no hay filtros de rango en la barra. Se conserva la forma vacia porque el
		servicio la sigue recibiendo, y asi deja de mandar esos parametros sin tener
		que tocar el servidor.
	*/
	const rangeFilterRef : any = {}

	/* La barra de jornadas salia en formato de 24 horas y en el plantel se habla
	   en am/pm. Aplica a cualquier plantel porque las horas salen de su ficha. */
	const horaEnDoce = (hora : number, minutos : number) : string =>
	{
		const sufijo = hora < 12 ? "am" : "pm"
		const docena = (hora % 12) === 0 ? 12 : (hora % 12)

		return sprintf("%d:%02d %s", docena, minutos, sufijo)
	}

	/* El plantel guarda el inicio de jornada como hora * 10 + minutos / 6. */
	const inicioDeJornada = (codigo : number) : string =>
	{
		const minutos = (codigo % 10) * 6
		const hora    = (codigo - (codigo % 10)) / 10

		return horaEnDoce(hora, minutos)
	}

	const procedureComplaint = (message : string)=>
	{
		dispatch
		(
			displayNotice
			({
				cornerClose : false,
				message     : message,
				heading     : <h3 style={{color : "var(--wa-rojo-error)", display : "inline-block"}}>
					Error
				</h3>
			})
		)
	}

	const clearSearch = ()=>
	{
		setCustomSearch(defaultSearch)
		setClearSearchRequested(true)
	}
	//FX---------------------------------------------------------------------------------------------------------------------
	useEffect
	(
		()=>
		{
			if(clearSearchRequested && _.isEqual(defaultSearch, customSearch))
			{
				search(customSearch.period)
			}
		},
		[customSearch, clearSearchRequested]
	)

	useEffect
	(
		()=>
		{
			const onLoad = async()=>
			{
				if(currentUserCampus == null)
				{
					procedureComplaint
					(
						"No hay planteles registrados o asignados al rol en uso por el usuario, " +
						"por lo que el acceso a este módulo permanecerá deshabilitado."
					)
					procedureComplaint
					(
						"No hay planteles registrados o asignados al rol en uso por el usuario, " +
						"por lo que el acceso a este módulo permanecerá deshabilitado."
					)
				}
				else
				{
					setInitSetup(COURSES)
				}
			}

			if(sessionToken)
			{
				onLoad()
			}
		},
		[]
	)

	useEffect
	(
		()=>
		{
			const onLoad = async()=>
			{
				switch(initSetup)
				{
					case COURSES:
						errorMessage = "La lista de cursos registrados no pudo ser extraída"
						dispatch(disableIdleLockSwitch())
						try
						{
							const expand : number = 0
							const criteria : any = null
							const result : any = await CoursesService.searchCourses(campusRef[currentUserCampus], criteria, expand)
							if(result.status == 200 || result.status == 204)
							{
								let courseIndex : any = {}
								let levelIndex : any = {}
								let kitIndex : any
								setCourses
								(
									result.data.map
									(
										(course : any)=>
										{
											courseIndex["C_" + course.id] = course
											course.levels.map
											(
												(level : any)=>
												{
													kitIndex = {}
													level.kits.map
													(
														(kit : any)=>
														{
															kitIndex["K_" + kit.id] = kit
														}
													)
													levelIndex["L_" + level.id] = {...level, kitRef : kitIndex, course : course}
												}
											)
											return {...course, levels : []}
										}
									)
								)
								setLevels(Object.values(levelIndex))
								setLevelRef(levelIndex)
								setCourseRef(courseIndex)
								setInitSetup(MENTORS)
							}
							else
							{
								procedureComplaint(errorMessage)
							}
						}
						catch(error)
						{
							console.log(error)
							procedureComplaint(errorMessage)
							dispatch(disableIdleLockSwitch())
						}
					break;
					case MENTORS:
						errorMessage = "La lista de profesores no pudo ser extraída"
						dispatch(enableIdleLockSwitch())
						try
						{
							const result = await EmployeesService.searchEmployees({roleIds : [baseRole.MENTOR]})
							if(result.status == 200 || result.status == 204)
							{
								setMentors(result.data.map((mentor : any) =>({...mentor, name : mentor.first_name + " " + mentor.last_name})))
								setInitSetup(PERIODS)
							}
							else
							{
								procedureComplaint(errorMessage)
							}
						}
						catch(error)
						{
							console.log(error)
							procedureComplaint(errorMessage)
							dispatch(disableIdleLockSwitch())
						}
					break
					case PERIODS:
						errorMessage = "La lista de periodos registrados no pudo ser extraída"
						try
						{
							const result = await PeriodsService.searchPeriods(campusRef[currentUserCampus].id)
							if(result.status == 200 || result.status == 204)
							{
								let periodIndex : any = {}
								setPeriods
								(
									result.data.map
									(
										(period : any)=>
										{
											periodIndex["P_" + period.id] = period
											return{
												...period, label : period.name +
												" [" +
													monthRef[parseInt(period.start.split("-")[1]) - 1] + " - " +
													monthRef[parseInt(period.end.split("-")[1]) - 1] +
												"]"
											}
										}
									)
								)

								setPeriodRef(periodIndex)
								setInitSetup(BUILDINGS)
							}
							else
							{
								procedureComplaint(errorMessage)
							}
						}
						catch(error)
						{
							console.log(error)
							procedureComplaint(errorMessage)
							dispatch(disableIdleLockSwitch())
						}
					break
					case BUILDINGS:
						if(periods.length < 1)
						{
							dispatch(disableIdleLockSwitch())
							procedureComplaint
								("No hay periodos registrados, por lo que el acceso a este módulo permanecerá deshabilitado")
						}
						else
						{
							errorMessage = "El listado de edificios y aulas no pudo ser extraído"
							try
							{
								let expand   : string = "classrooms"
								const result : any    = await BuildingsService.searchBuildings(campusRef[currentUserCampus].id, {}, expand)
								if(result.status == 200 || result.status == 204)
								{
									let buildingIndex : any = {}
									let classroomIndex : any = {}
									let dayArrange    : any = []
									setBuildings
									(
										result.data.map
										(
											(building : any)=>
											{
												building.classrooms.map
												(
													(classroom : any)=>
													{
														classroomIndex["C_" + classroom.id] = classroom
													}
												)
												buildingIndex["B_" + building.id] = building
												return{...building, classrooms : []}
											}
										)
									)
									setClassrooms(Object.values(classroomIndex))
									setClassroomRef(classroomIndex)
									setBuildingRef(buildingIndex)
									setYearsRange(basicInfo.futureRange)
									dayArrange.fill(undefined, 0, 29)//Half hours between 07:00 and 21:30 [(215 - 70)/10] * 2
									setDayDistribution(dayArrange)
									setCustomSearch(defaultSearch)
									setInitSetup(ENDING)
								}
								else
								{
									procedureComplaint(errorMessage)
								}
							}
							catch(error)
							{
								console.log(error)
								procedureComplaint(errorMessage)
								dispatch(disableIdleLockSwitch())
							}
						}
					break
					case ENDING:
						setSectionLoaded(true)
						search(customSearch.period)
					break
				}
			}

			onLoad()
		},
		[initSetup]
	)

	useEffect(()=>{setTotalRecords(data.length)}, [data])

	useEffect
	(
		()=>
		{
			if(clearSearchRequested && _.isEqual(defaultSearch, customSearch))
			{
				setClearSearchRequested(false)
				search(customSearch.period)
			}
		},
		[customSearch, clearSearchRequested]
	)

	useEffect
	(
		()=>
		{
			if(currentUserCampus && (currentUserCampus != userCampus))
			{
				setSectionLoaded(false)
				setCustomSearch(defaultSearch)
				setClassroomRef({})
				setDistributionRef({})
				setScheduleRef({})
				setCourseRef({})
				setLevelRef({})
				setPeriodRef({})
				localStorage.setItem("currentCampus", currentUserCampus)
				setCurrencyCode(basicInfo.currencyRef["C_" + campusRef[currentUserCampus].company.currency_id].code)
				setCurrencySymbol(basicInfo.currencyRef["C_" + campusRef[currentUserCampus].company.currency_id].symbol)
				setUserCampus(currentUserCampus)
				setInitSetup(COURSES)
			}
		},
		[currentUserCampus, sectionLoaded]
	)
	//-----------------------------------------------------------------------------------------------------------------------
	const periodSearch = (event : any)=>
	{
		if(event && event.target)
		{
			const period = event.target.value

			let startYear  : number
			let endYear    : number
			let noPeriod   : boolean = isEmpty(period)
			let minSetDate : Date
			let maxSetDate : Date

			if(noPeriod)
			{
				startYear = basicInfo.futureRange[0]
				endYear = basicInfo.futureRange[basicInfo.futureRange.length - 1]
				minSetDate = new Date(startYear + "/01/01 23:00:00")
				maxSetDate = new Date(endYear   + "/12/31 23:00:00")
			}
			else
			{
				startYear = parseInt(period.start.split("-")[0])
				endYear = parseInt(period.end.split("-")[0])
				minSetDate = (new Date(period.start.replaceAll("-", "/") + " 23:00:00"))
				maxSetDate = (new Date(period.end.replaceAll("-", "/") + " 23:00:00"))
			}

			setMinDate(minSetDate)
			setMaxDate(maxSetDate)
			setYearsRange
			(
				startYear == endYear
					? [startYear]
						: (noPeriod ? basicInfo.futureRange : Array((endYear - startYear) + 1).fill(undefined).map((element, index) => index + startYear))
			)

			search(period, currentDay)
		}
	}

	const search = async (period : any, date? : any)=>
	{
		const searchError : string  = "La búsqueda no pudo ser completada"
		let proceedingSearch : boolean = true
		let newSearch : any = {...customSearch, period : period}
		setCustomSearch(newSearch)
		if(period && period.id)
		{
			const periodStart : any = new Date(period.start + " 23:00:00")
			const periodEnd   : any = new Date(period.end   + " 23:00:00")
			if(Math.floor((date - periodStart) / 86400000) == -1)
			{
				if(!notADayBefore)
				{
					setNotADayBefore(true)
					setTimeout(() => setNotADayBefore(false), 3000)
				}
			}
			else
			{
				if(Math.floor((date - periodEnd) / 86400000) == 1)
				{
					if(!notADayAfter)
					{
						setNotADayAfter(true)
						setTimeout(() => setNotADayAfter(false), 3000)
					}
				}
			}
		}
		if(!proceedingSearch)
		{
			setCurrentDay(date)
			setCurrentWeekDay(retardWeekDayOffset[date.getDay()])
		}
		else
		{
			try
			{
				dispatch(enableIdleLockSwitch())
				/*
					Al servidor se le pide el DIA COMPLETO y nada mas: periodos, profesores,
					cursos, edificios y aulas se filtran aqui con lo que ya llego. Asi
					marcar un filtro no dispara otra consulta y la pantalla responde al
					instante. (Esteban, 2026-09-21)
				*/
				const result = await CalendarService.getDailyView
				(
					date,
					{...newSearch, period : null, periods : [], mentors : [], courses : [], buildings : [], classrooms : []},
					rangeFilterRef
				)
				if(result.status == 200 || result.status == 204)
				{
					setCustomSearch(newSearch)
					setCurrentSearch(newSearch)

					if(date == "today" && result.data.itIsToday == false)
					{
						setNotToday(true)
						setTimeout(() => setNotToday(false), 3000)
					}
					else
					{
						/*
							A structure is prepared containing all involved buildings referred by a key (string "B_" followed by its id
							Inside each classroom there is an array of a full day 
							Finally that array is dumped into an array of 3 arrays to enqueue all schedules for each turn (morning, afternoon and evening).
						*/
						let distributionIndex : any = {}
						let ordenIndex        : any = {}
						let scheduleIndex : any = {}
						let index : number
						let startingHour : number =  70
						let i : number
						let sessionIndex : any
						let day : Date = new Date(result.data.date + " 23:00:00")
						let attendanceMap : any
						let displayUnrestricted : boolean = unrestrictedResultsDisplay(newSearch)
						let currentDayCount : number = 0
						let tuitionIndebtedCount : number
						let availableSpace : any = null
						let availableSpacesCount : number = 0
						let availableSpaces : any = []
						let studentRef : any = {}
						let availableSpaceIndex : any = {}
						let externalIndex : number = 0
						let length : number = 0
						let hours : number
						let level : any
						let classroom : any
						let distributionKeys : string[]
						let shiftIndexListing : number[]=
						[
							(campusRef[currentUserCampus].afternoon_shift_start - 70) / 5,
							(campusRef[currentUserCampus].evening_shift_start - 70) / 5
						]//Positions for 120 and 160 code hours (12:00 and 16:00 respectively)
						setUnrestrictedDisplay(displayUnrestricted)
						setToday(new Date(result.data.today + " 23:00:00"))
						setCurrentDay(day)
						setCurrentWeekDay(retardWeekDayOffset[day.getDay()])
						if(result.data.sessions.length > 0)
						{
							if(displayUnrestricted)
							{
								if(newSearch.buildings.length < 1)
								{
									Object.keys(classroomRef).map
									(
										(key : string)=>
										{
											if(distributionIndex[key] == null)
											{
												distributionIndex[key]=
												{
													...classroomRef[key], turnList : [[], [], []],
													day : [],
													empty : true
												}
											}
										}
									)
								}
								else
								{
									newSearch.buildings.map
									(
										(building : any)=>
										{
											if(building.classrooms.length > 0)
											{
												building.classrooms.map
												(
													(classroom : any)=>
													{
														if(distributionIndex["C_" + classroom.id] == null)
														{
															distributionIndex["C_" + classroom.id]=
															{
																...classroomRef["C_" + classroom.id], turnList : [[], [], []],
																day : [],
																empty : true
															}
														}
													}
												)
											}
											else
											{
												buildingRef["B_" + building.id].classrooms.map
												(
													(classroom : any)=>
													{
														if(distributionIndex["C_" + classroom.id] == null)
														{
															distributionIndex["C_" + classroom.id]=
															{
																...classroomRef["C_" + classroom.id], turnList : [[], [], []],
																day : [],
																empty : true
															}
														}
													}
												)
											}
										}
									)
								}
							}
							result.data.sessions.map
							(
								(session : any)=>
								{
									if(scheduleIndex["S_" + session.schedule_id] == null)
									{
										if(distributionIndex["C_" + session.schedule.classroom_id].empty)
										{
											distributionIndex["C_" + session.schedule.classroom_id].empty = false
										}
										sessionIndex = {}
										session.schedule.sessions.map
										(
											(session : any)=>
											{
												sessionIndex["S_" + session.week_day] = session
											}
										)
										studentRef = {}
										tuitionIndebtedCount = 0
										session
											.schedule
											.enrollments
											.sort((a : any, b : any) => +(a.created_at < b.created_at) - +(a.created_at > b.created_at))
											.filter((enrollment : any) => enrollment)
											.map
											(
												(enrollment : any)=>
												{
													if(+enrollment.expiredMonthlyEnrollmentBalance > 0 && enrollment.status_id != ENROLLMENT_STATUS.NOT_STARTED)
													{
														tuitionIndebtedCount++
													}
													if(studentRef["S_" + enrollment.student_id])
													{
														studentRef["S_" + enrollment.student_id].enrollments.push(enrollment)
													}
													else
													{
														if(enrollment.status_id == ENROLLMENT_STATUS.ACTIVE)
														{
															studentRef["S_" + enrollment.student_id]=
															{
																...enrollment.student, enrollments : [enrollment]
															}
														}
													}
												}
											)
										attendanceMap = {}
										session.schedule.attendances.map
										(
											(attendance : any)=>
											{
												//La tabla guarda bool_is_present; status_id no existe alli.
												attendanceMap["S_" + attendance.student_id] = attendance.bool_is_present ? 1 : 0
											}
										)
										for(let thisLevel of levels)
										{
											if(thisLevel.id == session.schedule.level_id)
											{
												level = thisLevel
												break
											}
										}
										for(let thisClassroom of classrooms)
										{
											if(thisClassroom.id == session.schedule.classroom_id)
											{
												classroom = thisClassroom
												break
											}
										}
										scheduleIndex["S_" + session.schedule_id]=
										{
											...session.schedule, students : Object.values(studentRef),
											tuitionIndebtedCount : tuitionIndebtedCount,
											attendanceRef : attendanceMap,
											sessionRef : sessionIndex,
											/*
												El desplegable de saldos abria cerrado, asi que habia que
												picar la flechita por horario para ver quien debe. Ahora
												abre abierto y la flecha sirve para esconder.
												(Esteban, 2026-09-16)
											*/
											tuitionIndebtedShown : true,
											level : {...level},
											classroom : classroom
										}
									}
									startingHour = session.start_hours
									i = startingHour
									do
									{
										index = (i - 70) / 5
										distributionIndex["C_" + session.schedule.classroom_id].day[index] = "S_" + session.schedule_id
										if(ordenIndex["S_" + session.schedule_id] == null)
										{
											ordenIndex["S_" + session.schedule_id] = index
										}
										i += 5
									}
									while(i < session.end_hours)
									return session
								}
							)
							//Use of any of the function contemplated filters makes the available hours space display to be skipped
							distributionKeys = Object.keys(distributionIndex)
							if(displayUnrestricted)
							{
								if(distributionKeys.length > 0)
								{
									distributionKeys.map
									(
										(key : string)=>
										{
											if(availableSpace)
											{
												availableSpace.end_hours = ((externalIndex + 1) * 5) + 70
												availableSpaceIndex["A_" + availableSpacesCount] = availableSpace
												availableSpaces.push(availableSpace)
												availableSpacesCount++
												availableSpace = null
											}
											for(i = 0; i < DAY_HALF_HOURS; i++)//30
											{
												externalIndex = i
												if(distributionIndex[key].day[i] == null)
												{
													if(availableSpace == null)
													{
														hours = (externalIndex * 5) + 70
														availableSpace=
														{
															classroom   : classroomRef[key],
															start_hours : (externalIndex * 5) + 70
														}
													}
													distributionIndex[key].day[i] = availableSpacesCount
												}
												else
												{
													if(availableSpace)
													{
														availableSpace.end_hours                         = (externalIndex * 5) + 70
														availableSpaceIndex["A_" + availableSpacesCount] = availableSpace
														availableSpaces.push(availableSpace)
														availableSpacesCount++
														availableSpace = null
													}
												}
											}
											if(availableSpace)
											{
												availableSpace.end_hours                         = ((externalIndex + 1) * 5) + 70
												availableSpaceIndex["A_" + availableSpacesCount] = availableSpace
												availableSpaces.push(availableSpace)
												availableSpacesCount++
												availableSpace = null
											}
										}
									)
								}
								if(availableSpace)
								{
									availableSpace.end_hours                         = ((externalIndex + 1) * 5) + 70
									availableSpaceIndex["A_" + availableSpacesCount] = availableSpace
									availableSpaces.push(availableSpace)
									availableSpacesCount++
									availableSpace = null
								}
							}
							distributionKeys.map
							(
								(key : string)=>
								{
									let j : number = 0
									for(i = 0; i < DAY_HALF_HOURS; i++)//30
									{
										if(i == shiftIndexListing[0] || i == shiftIndexListing[1])
										{
											j++
										}
										length = distributionIndex[key].turnList[j].length
										if(length < 1 || distributionIndex[key].turnList[j][length -1] != distributionIndex[key].day[i])
										{
											distributionIndex[key].turnList[j].push(distributionIndex[key].day[i])
										}
									}
								}
							)
							if(Object.keys(distributionIndex).length < 1 || (!unrestrictedResultsDisplay(customSearch) && Object.keys(scheduleIndex).length < 1))
							{
								if(!noResultsFound)
								{
									setNoResultsFound(true)
									setTimeout(() => setNoResultsFound(false), 3000)
								}
							}
						}
						setDayAttendanceRef({...result.data.attendanceDays})
						setCurrentDayCountInPeriod(currentDayCount)
						setScheduleRef({...scheduleIndex, ...availableSpaceIndex})
						setDistributionRef({...distributionIndex})
						setOrdenRef({...ordenIndex})
						setSpaceRef({...availableSpaceIndex})
						setSectionLoaded(true)
					}
				}
				else
				{
					procedureComplaint(searchError)
				}
			}
			catch(error : any)
			{
				console.log(error)
				if(error.response.status == 400)
				{
					setNotToday(true)
					setDistributionRef({})
					setCurrentDay(date)
					setCurrentWeekDay(retardWeekDayOffset[date.getDay()])
					setTimeout(() => setNotToday(false), 3000)
				}
				else
				{
					procedureComplaint(searchError)
				}
			}
			finally
			{
				dispatch(disableIdleLockSwitch())
			}
		}
	}

	const getScheduleAttendances = async (schedule : any, index? : number)=>
	{
		const startYear : number = parseInt
		(
			schedule
				.period
				.start
				.split("/")[0]
		)

		const endYear   : number = parseInt
		(
			schedule
				.period
				.end
				.split("/")[0]
		)

		setYearsRange
		(
			startYear == endYear ? [startYear] : Array(endYear - startYear).fill(undefined).map((element, index) => index + startYear)
		)

		let   hours               : number
		let   minutesPart         : number
		let   dateSelectedWeekDay : number
		const searchError         : string = "La consulta de horarios de asistencia solicitada no pudo ser ejecutada"

		try
		{
			dispatch(enableIdleLockSwitch())

			const result = await AttendancesService.getScheduleAttendances(campusRef[currentUserCampus].id, schedule.id)

			if(result.status == 200|| result.status == 204)
			{
				if((result.data.regularAttendances.length + result.data.compensationAttendances.length) < 1)
				{
					setNoAttendancesFoundOn("S_" + schedule.id)
					setTimeout(() => setNoAttendancesFoundOn(null), 3000)
				}
				else
				{
					let dateListing              : any = {}
					let monthDateCounting        : any = {}
					let attendanceStudentIndex   : any = {}
					let studentIndex             : any = {}
					let compensationDayList      : any = {}
					let datePart                 : string
					let monthPart                : string
					let dateKeys                 : string[]
					let ymd                      : any
					let compensateDayStudIndex   : any = {}
					let attendanceCountIndex     : any = {}

					result.data.regularAttendances.map
					(
						(attendance : any)=>
						{
							datePart  = attendance.attendance_datetime.split(" ")[0]
							ymd       = datePart.split("-")
							monthPart = monthRef[parseInt(ymd[1]) - 1]

							if(dateListing[datePart] == null)
							{
								dateListing[datePart] = weekDays
									[retardWeekDayOffset[(new Date(attendance.attendance_datetime)).getDay()]].substring(0, 2) + ymd[2]

								if(monthDateCounting[monthPart] == null)
								{
									monthDateCounting[monthPart] = 0
								}

								monthDateCounting[monthPart]++
							}

							if(attendanceCountIndex["S_" + attendance.student_id] == null)
							{
								attendanceCountIndex["S_" + attendance.student_id] = 0
							}

							if(attendance.status_id != attendanceStatus.ABSENT)
							{
								attendanceCountIndex["S_" + attendance.student_id]++
							}

							attendanceStudentIndex[datePart + "S_" + attendance.student_id] = attendance.status_id
						}
					)

					schedule.enrollments.map
					(
						(enrollment : any)=>
						{
							if(attendanceCountIndex["S_" + enrollment.student_id] == null)
							{
								attendanceCountIndex["S_" + enrollment.student_id] = 0
								studentIndex["S_" + enrollment.student_id]         = enrollment.student
							}
						}
					)

					result.data.compensationAttendances.map
					(
						(attendance : any)=>
						{
							datePart  = attendance.attendance_datetime.split(" ")[0]
							ymd       = datePart.split("-")
							monthPart = monthRef[parseInt(ymd[1]) - 1]

							if(dateListing[datePart] == null)
							{
								dateListing[datePart] = weekDays[retardWeekDayOffset[(new Date(attendance.attendance_datetime)).getDay()]].substring(0, 2) + ymd[2]

								if(monthDateCounting[monthPart] == null)
								{
									monthDateCounting[monthPart] = 0
								}

								monthDateCounting[monthPart]++
							}

							attendanceStudentIndex[datePart + "S_" + attendance.student_id] = attendance.status_id

							if(compensationDayList[datePart] == null)
							{
								compensationDayList[datePart] = []
							}

							compensationDayList[datePart].push("S_" + attendance.student_id)
						}
					)

					dateKeys = Object.keys(dateListing).sort()

					setDateRef(dateListing)
					setMonthDateCount(monthDateCounting)
					setAttendanceCountRef(attendanceCountIndex)
					setDateList(dateKeys)
					setDateCount(dateKeys.length)
					setAttendanceStudentRef(attendanceStudentIndex)
					setCompensationDayStudentRef(compensateDayStudIndex)
					setCompensationStudentRef(result.data.compensationStudentRef)

					setSelectedSchedule
					({
						...schedule, studentRef : studentIndex,
						compensationStudents    : result.data.compensationstudents
					})
				}
			}
			else
			{
				procedureComplaint(searchError)
			}
		}
		catch(error)
		{
			console.log(error)

			procedureComplaint(searchError)
		}
		finally
		{
			dispatch(disableIdleLockSwitch())
		}
	}

	const promptEnrolling = async(course : any, level : any, schedule : any)=>
	{
		let retrievalError : string = "La lista de matrículas no pudo ser extraída"

		try
		{
			dispatch(enableIdleLockSwitch())

			const result = await HttpManager.getInstance().enrollmentService.searchEnrollments(campusRef[currentUserCampus].id, schedule.id)

			if(result.status == 200 || result.status == 204)
			{
				setSelectedEnrollment(enrollingSet(course, level, schedule, result.data, campusRef[currentUserCampus].company_id, basicInfo.articleTypeRef))
			}
			else
			{
				procedureComplaint(retrievalError)
			}
		}
		catch(error)
		{
			console.log(error)
			procedureComplaint(retrievalError)
		}
		finally
		{
			dispatch(disableIdleLockSwitch())
		}
	}

	const DateInput = forwardRef
	(
		({value, onClick} : any, ref : ForwardedRef<HTMLButtonElement>)=>
		{
			const dayLabel   : string = weekDays[retardWeekDayOffset[currentDay.getDay()]]
			const monthLabel : string = monthRef[currentDay.getMonth()]
			const yearLabel  : string = currentDay.getFullYear() + ""
			return (currentDay) && <button className="text-center btn btn-default" ref={ref} onClick={onClick} type="button"
			  style={{paddingTop : "15px", height : "100%", border : "1px solid var(--wa-gris)"}} disabled={idleLocked}
			><h4>
				<span className="d-xl-none d-lg-none d-md-none d-sm-none d-xs-none">
					Calendario
				</span>
				<span style={{display : "inline-block", color : "var(--wa-apagado)"}}>
					<span className="d-none d-md-inline-block">
						{dayLabel.substring(0, 2)}
					</span>
					<span className="d-none d-lg-inline-block">
						{dayLabel.substring(2)}
					</span>
					&nbsp;
					{sprintf("%02d", currentDay.getDate())}
					&nbsp;
					<span>
						{monthLabel.substring(0, 3)}
					</span>
					<span className="d-none d-lg-inline-block">
						{monthLabel.substring(3)}
					</span>
					&nbsp;
					<span className="d-none d-lg-inline-block">
						{yearLabel.substring(0, 2)}
					</span>
					<span>
						{yearLabel.substring(2)}
					</span>
				</span>
			</h4></button>
		}
	)
 
	/*
		LAS TRES COLUMNAS DEL DIA, SIN AULAS.

		Antes la tabla tenia una fila POR AULA y tres celdas (una por jornada). Un
		aula con clase solo en la Jornada 2 dejaba vacias la 1 y la 3: de ahi los
		huecos. Y con 500px fijos por columna habia que arrastrar de lado.

		Ahora se aplanan las tres listas: cada jornada junta las clases de todas
		las aulas, una tras otra, ordenadas por la hora en que arrancan. Los
		bloques de "espacio libre" (los numericos) siguen entrando cuando el ojo
		esta encendido, y cada uno dice de que aula es. (Esteban, 2026-09-20)
	*/
	/*
		LO QUE SE PUEDE ELEGIR EN LOS FILTROS: solo lo que tiene clase ESTE dia.

		La barra ofrecia todos los periodos, todos los profesores y todos los
		cursos del plantel, aunque ese dia no hubiera ni uno. Estas listas salen de
		lo que ya vino del servidor, asi que no cuesta ninguna consulta.
		(Esteban, 2026-09-21)
	*/
	const opcionesDelDia = useMemo
	(
		()=>
		{
			const periodos : any = {}
			const maestros : any = {}
			const cursos   : any = {}
			const edificios: any = {}
			const aulas    : any = {}

			Object.keys(scheduleRef).forEach((clave : string) =>
			{
				const horario = scheduleRef[clave]

				if(horario == null) return

				//El horario trae period_id; el periodo entero vive en periodRef.
				const periodo = periodRef["P_" + horario.period_id]

				if(periodo && periodo.id)
				{
					periodos["P_" + periodo.id] = {id : periodo.id, name : periodo.name, start : periodo.start}
				}

				if(horario.mentor_id && horario.mentor)
				{
					maestros["M_" + horario.mentor_id] =
					{
						id   : horario.mentor_id,
						name : [horario.mentor.first_name, horario.mentor.last_name].filter(Boolean).join(" ")
					}
				}

				const nivel = levelRef["L_" + horario.level_id]
				const curso = nivel == null ? null : courseRef["C_" + nivel.course_id]

				if(curso) cursos["C_" + curso.id] = {id : curso.id, name : curso.name, levels : []}

				const aula = classroomRef["C_" + horario.classroom_id]

				if(aula)
				{
					aulas["A_" + aula.id] = {id : aula.id, name : aula.name, buildingId : aula.building_id}

					const edificio = buildingRef["B_" + aula.building_id]

					if(edificio) edificios["B_" + edificio.id] = {id : edificio.id, name : edificio.name, classrooms : []}
				}
			})

			//Los periodos, del mas viejo al mas nuevo; el resto por nombre.
			const porNombre = (a : any, b : any) => String(a.name).localeCompare(String(b.name))

			return {
				periodos  : Object.values(periodos).sort((a : any, b : any) => String(a.start).localeCompare(String(b.start))),
				maestros  : Object.values(maestros).sort(porNombre),
				cursos    : Object.values(cursos).sort(porNombre),
				edificios : Object.values(edificios).sort(porNombre),
				aulas     : Object.values(aulas).sort(porNombre)
			}
		},
		[scheduleRef, levelRef, courseRef, classroomRef, buildingRef, periodRef]
	)

	/*
		Los filtros se aplican AQUI, no en el servidor: el dia entero ya esta
		cargado, asi que marcar un profesor o un aula responde al instante y no
		vuelve a pedir nada. (Esteban, 2026-09-21)
	*/
	const pasaLosFiltros = useMemo
	(
		() => (clave : string) : boolean =>
		{
			const horario = scheduleRef[clave]

			if(horario == null) return true

			const algunoDe = (elegidos : any[], valor : any) =>
				elegidos.length < 1 || elegidos.some((x : any) => x && x.id === valor)

			if(!algunoDe(customSearch.periods || [], horario.period_id)) return false
			if(!algunoDe(customSearch.mentors || [], horario.mentor_id)) return false

			const nivel = levelRef["L_" + horario.level_id]

			if(!algunoDe(customSearch.courses || [], nivel?.course_id)) return false

			const aula = classroomRef["C_" + horario.classroom_id]

			if(!algunoDe(customSearch.buildings || [], aula?.building_id)) return false
			if(!algunoDe(customSearch.classrooms || [], horario.classroom_id)) return false

			if(customSearch.attendanceState && customSearch.attendanceState !== "todas")
			{
				const hecha = dayAttendanceRef[clave] ? dayAttendanceRef[clave][2] === true : false

				if(customSearch.attendanceState === "hechas" && !hecha) return false
				if(customSearch.attendanceState === "faltan"  &&  hecha) return false
			}

			return true
		},
		[scheduleRef, levelRef, classroomRef, dayAttendanceRef, customSearch]
	)

	const jornadasDelDia : any[][] = useMemo
	(
		()=>
		{
			const jornadas : any[][] = [[], [], []]
			const vistos   : any[]   = [new Set(), new Set(), new Set()]

			Object.keys(distributionRef).forEach
			(
				(aula : string) => (distributionRef[aula].turnList || []).forEach
				(
					(lista : any[], jornada : number) => (lista || []).forEach
					(
						(clave : any)=>
						{
							if(clave == null || jornada > 2)
							{
								return
							}
							//Los huecos libres solo cuando se piden.
							if(!isNaN(clave) && (!unrestrictedDisplay || !availabilityDisplay))
							{
								return
							}
							if(vistos[jornada].has(clave))
							{
								return
							}
							//Los filtros de la barra, aplicados aqui mismo.
							if(isNaN(clave) && !pasaLosFiltros(clave))
							{
								return
							}
							vistos[jornada].add(clave)
							jornadas[jornada].push(clave)
						}
					)
				)
			)

			const arranque = (clave : any) : number => isNaN(clave)
				? (ordenRef[clave] ?? 999)
				: ((spaceRef["A_" + clave]?.start_hours ?? 999) - 70) / 5

			return jornadas.map((lista : any[]) => lista.sort((a, b) => arranque(a) - arranque(b)))
		},
		[distributionRef, ordenRef, spaceRef, unrestrictedDisplay, availabilityDisplay, pasaLosFiltros]
	)

	return !sectionLoaded ? "" : <>
		<div style={{margin : "0px", padding : "15px"}}>
			<table><tbody><tr>
				<td className="d-none d-sm-table-cell d-md-table-cell d-lg-table-cell d-xl-table-cell"
				  style={{verticalAlign : "bottom", paddingRight : "5px"}} 
				><h1>
					Calendario
				</h1></td>
				<td className="d-none d-sm-table-cell d-md-table-cell d-lg-table-cell d-xl-table-cell" style={{width : "50px"}} />
				<td className="d-none d-sm-table-cell d-md-table-cell d-lg-table-cell d-xl-table-cell"
				  style={{paddingTop : "5px"}}
				>
					<Popover positions={["bottom"]} isOpen={notToday} content=
					  {<div style={{background : "var(--second-bg)", padding : "5px 5px 0px 5px"}}>
						<div className="requirementAlert">
							<FontAwesomeIcon icon={solid("exclamation")}/>
						</div>
						&nbsp;
						El día se encuentra fuera del periodo seleccionado
					  </div>}
					><button style={{width : "100px", height : "50px", border : "1px solid var(--wa-negro)"}} onClick={() => search(customSearch.period, "today")}
					  disabled={idleLocked || notToday} className="btn btn-default rounded-pill" type="button"
					>
						Hoy
					</button></Popover>
				</td>
				<td style={{width : "50px"}} className="d-none d-sm-table-cell d-md-table-cell d-lg-table-cell d-xl-table-cell" />
				<td className="d-none d-sm-table-cell d-md-table-cell d-lg-table-cell d-xl-table-cell"><Popover positions={["bottom"]}
				  isOpen={notADayBefore || notADayAfter} content=
				  {<div style={{background : "var(--second-bg)", padding : "5px 5px 0px 5px"}}>
					<div className="requirementAlert">
						<FontAwesomeIcon icon={solid("exclamation")}/>
					</div>
					&nbsp;
					El día {notADayBefore ? "anterior" :  "posterior"} se encuentra fuera del periodo seleccionado
				  </div>}
				><div style={{display : "flex"}}>{
					count.map
					(
						(step : number, index : number) => <button key={"D" + step} style={{color : "var(--wa-apagado)"}} type="button"
						  className="btn btn-default" disabled={idleLocked} onClick=
						  {
							()=>
							{
								let date = new Date(currentDay)

								date.setDate(currentDay.getDate() + step)
								search(customSearch.period, new Date(date))
							}
						  }
						><b>
							<FontAwesomeIcon icon={icons[index]} size="2x" />
						</b></button>
					)
				}</div></Popover></td>
				<td>{
					((yearsRange && yearsRange.length < 1) || currentDay == null) ? "" : <LeDatePicker disabled={idleLocked}
					  onChange={(e : any) => search(customSearch.period, e.target.value)} months={monthRef} name="birthdayValue"
					  maxDate={maxDate} years={yearsRange} value={currentDay} minDate={minDate} customInput={<DateInput />}
					  id="birthdayValue" required
					/>
				}</td>
			</tr></tbody></table>
			{/*
				Iba clavado con position fixed y 310px de ancho: en la computadora eso
				lo mantiene a la vista mientras se recorre la columna de filtros, pero
				en un telefono queda flotando encima del contenido y con un ancho que
				no corresponde a nada. En telefono se deja en el flujo normal y a todo
				lo ancho. (Esteban, 2026-09-16)
			*/}
			<div style=
			  {{
				display    : "inline-block",
				position   : esTelefono ? "static" : "fixed",
				zIndex     : 2,
				background : "var(--second-bg)",
				width      : esTelefono ? "100%" : "310px",
				paddingTop : "15px"
			  }}
			>
				<table><tbody><tr>
					<td style={{width : "100%"}}><Popover positions={["bottom"]} isOpen={noResultsFound} content=
					  {<div style={{background : "var(--second-bg)", padding : "5px 5px 0px 5px"}}>
						<div className="requirementAlert">
							<FontAwesomeIcon icon={solid("exclamation")}/>
						</div>
						&nbsp;
						Sin resultados
					  </div>}
					><button
					  style={{border : "1px solid var(--wa-negro)", width : "100%"}}
					  onClick={() => search(customSearch.period, currentDay)}
					  className="btn btn-default"
					  type="button"
					  disabled={idleLocked}
					>
						Buscar
						&nbsp;
						<FontAwesomeIcon icon={solid("magnifying-glass-arrow-right")} />
					</button></Popover></td>
					<td><div style={{width : "50px"}}>{
						_.isEqual(currentSearch, customSearch) && _.isEqual(currentSearch, defaultSearch) ? "" : <button
						  type="button"
						  className="btn btn-default"
						  disabled={idleLocked}
						  onClick={clearSearch}
						>
							<FontAwesomeIcon icon={solid("times")} />
						</button>
					}</div></td>
				</tr></tbody></table>
			</div>
		</div>
		<div className={esTelefono ? "calDiaCuerpo telefono" : "calDiaCuerpo"}
		  style={{display : "flex", height : "calc(100vh - 149px)"}}
		>
			{
				!esTelefono ? null : <div className="calDiaBarra">
					<button type="button" className={filtrosAbiertos ? "waAccionFila principal" : "waAccionFila"}
					  onClick={() => setFiltrosAbiertos(!filtrosAbiertos)}
					>
						{filtrosAbiertos ? "Cerrar filtros" : "Filtros"}
					</button>
					<div className="calDiaJornadas">{
						[
							campusRef[currentUserCampus].morning_shift_name,
							campusRef[currentUserCampus].afternoon_shift_name,
							campusRef[currentUserCampus].evening_shift_name
						].map((nombre : string, i : number) =>
							<button key={"j_" + i} type="button"
							  className={jornadaVisible === i ? "calDiaJornada activa" : "calDiaJornada"}
							  onClick={() => setJornadaVisible(i)}
							>
								{nombre}
							</button>)
					}</div>
				</div>
			}
			{esTelefono && !filtrosAbiertos ? null :
			<CalendarSideFilter distributionRef={distributionRef} searchFn={search} periods={periods} periodRef={periodRef}
			  courses={courses} courseRef={courseRef} buildings={buildings} buildingRef={buildingRef} customSearch={customSearch}
			  searchChangeFn={handleSearchChange} mentors={mentors} scheduleRef={scheduleRef} periodSearch={periodSearch}
			  unrestrictedDisplay={unrestrictedDisplay} availabilityDisplay={availabilityDisplay}
			  setAvailabilityDisplay={setAvailabilityDisplay} opcionesDelDia={opcionesDelDia}
			/>}
			{/*
				Sin ancho minimo de 1.500px y sin arrastrar de lado: las tres jornadas
				se reparten el ancho que haya y lo que sobra se recorre hacia abajo.
				(Esteban, 2026-09-20)
			*/}
			<div className="calDiaTabla"
			  style=
			  {{
					height     : "calc(100vh - 149px)",
					/*
						Los 14px de la derecha son el mismo hueco que separa las tres jornadas
						entre si: sin ellos la tercera quedaba pegada al filo de la ventana.
						(Esteban, 2026-09-21)
					*/
					/* Flex y no 100vw: con el menu fijado el contenido ya es 300px mas angosto. (Esteban, 2026-10-22) */
					...(esTelefono ? {width : "100%"} : {flex : "1 1 0", minWidth : 0}),
					paddingRight : esTelefono ? "0px" : "14px",
					overflowY    : "auto",
					overflowX    : "hidden"
			  }}
			><table
			  style={{background : "var(--wa-papel-3)", width : "100%", tableLayout : "fixed"}}
			><tbody>
				<tr style={{height : "0px"}}>
					{esTelefono && jornadaVisible !== 0 ? null :
					<td style={{background : "var(--wa-superficie)", textAlign : "center", padding: "5px", color : "var(--wa-acento-texto)", fontWeight : 700, border : "1px solid var(--wa-acento)", borderBottom : "0px"}}>
						{campusRef[currentUserCampus].morning_shift_name}
						<br />
						{horaEnDoce(7, 0)}
						&nbsp;
						-
						&nbsp;
						{inicioDeJornada(campusRef[currentUserCampus].afternoon_shift_start)}
					</td>}
					{esTelefono ? null : <td style={{width : "14px"}} />}
					{esTelefono && jornadaVisible !== 1 ? null :
					<td style={{background : "var(--wa-superficie)", textAlign : "center", padding: "5px", color : "var(--wa-acento-texto)", fontWeight : 700, border : "1px solid var(--wa-acento)", borderBottom : "0px"}}>
						{campusRef[currentUserCampus].afternoon_shift_name}
						<br />
						{inicioDeJornada(campusRef[currentUserCampus].afternoon_shift_start)}
						&nbsp;
						-
						&nbsp;
						{inicioDeJornada(campusRef[currentUserCampus].evening_shift_start)}
					</td>}
					{esTelefono ? null : <td style={{width : "14px"}} />}
					{esTelefono && jornadaVisible !== 2 ? null :
					<td style={{background : "var(--wa-superficie)", textAlign : "center", padding: "5px", color : "var(--wa-acento-texto)", fontWeight : 700, border : "1px solid var(--wa-acento)", borderBottom : "0px"}}>
						{campusRef[currentUserCampus].evening_shift_name}
						<br />
						{inicioDeJornada(campusRef[currentUserCampus].evening_shift_start)}
						&nbsp;
						-
						&nbsp;
						{horaEnDoce(22, 0)}
					</td>}
				</tr>
				{
					<tr>{
							jornadasDelDia.map
							(
								(turn : any, index : number) => esTelefono && index !== jornadaVisible ? null :
								<React.Fragment key={"J_" + index}>
									{index < 1 || esTelefono ? "" : <td style={{display : "table-cell", width : "14px"}} />}
									{/* Relleno a los lados: las tarjetas iban pegadas al filo de la columna. (Esteban, 2026-10-22) */}
									<td style={{border : "1px solid var(--wa-acento)", display : "table-cell", width : esTelefono ? "100%" : "33%", verticalAlign : "top"}}>
										<div style={{display : "flex", flexDirection : "column", alignItems : "stretch"}}>{
											turn.length < 1 ? "" : turn.filter((key : any) => key != null).map
											(
												(key : any) => <div style={{padding : "5px 6px", width : "100%", flex : 1}} key={key + "T_" + index + key}>
													<div style={{border : "1px solid var(--wa-acento)", borderRadius : "5px", height : "100%", display : "flex", flexDirection : "column", alignItems : "stretch"}}>{
														isNaN(key)
															?
														<div style={{background : "var(--wa-superficie)", borderTop : "10px solid var(--wa-acento)", flex : 1}}>
															<div style={{padding : "15px", display : "flex", flexDirection : "column", alignItems : "stretch", height : "100%"}}>
																<table style={{fontWeight : "900", width : "100%"}}><tbody>
																	<tr>
																		<td style={{verticalAlign : "top", paddingRight : "10px"}}>
																			<img src={courseRef["C_" + levelRef["L_" + scheduleRef[key].level_id].course_id].image || courseImage}
																			  style={{width : "25px", borderRadius : "50%", border : "3px solid var(--wa-acento)"}}
																			/>
																		</td>
																		<td colSpan={2} style={{width : "100%"}}>
																			<div style={{display : "inline-block"}}>
																				<span className={"badge bg-" + (scheduleRef[key].status_id == scheduleStatus.ACTIVE ? "primary" : "secondary")}
																				  style={{position : "relative", bottom : "3px"}}
																				>
																					{basicInfo.scheduleStatusRef["SS_" + scheduleRef[key].status_id].name}
																				</span>
																			</div>
																			&nbsp;
																			{/*
																				Salia el nombre largo del curso, luego el del modulo y
																				luego el del horario, y mas abajo otra vez la hora en
																				formato de 24. Ahora: nombre corto del curso, periodo,
																				y el horario una sola vez ya ordenado en am/pm.
																				(Esteban, 2026-09-16)
																			*/}
																			{cursoCorto(courseRef["C_" + levelRef["L_" + scheduleRef[key].level_id].course_id].name)}
																			<div style={{color : "var(--wa-acento-texto)"}}>
																				<div>
																					{periodRef["P_" + scheduleRef[key].period_id].name}
																				</div>
																			</div>
																			<div style={{color : "var(--wa-apagado)"}}>
																				{horarioBonito(scheduleRef[key].name)}
																			</div>
																		</td>
																	</tr>
																	<tr>
																		<td style={{color : "var(--wa-apagado)", height : "38px"}}>
																			<FontAwesomeIcon icon={solid("chalkboard-user")} />
																		</td>
																		<td style={{width : "100%"}}>
																			<span style={{color : "var(--wa-apagado)"}}>
																				Profesor:
																				&nbsp;
																			</span>
																			{
																				scheduleRef[key].mentor == null ? "-" :
																					nombreDeProfesor
																					(
																						scheduleRef[key].mentor.first_name,
																						scheduleRef[key].mentor.last_name
																					)
																			}
																		</td>
																		<td style={{width : "1px"}}>{
																			scheduleRef[key].mentor == null ? "" : <button
																			  disabled={idleLocked} className="btn btn-default"
																			  type="button" onClick=
																			  {
																				() => setSelectedUser
({
	...scheduleRef[key].mentor, employeeView : true,
	imagePreview                             : scheduleRef[key].mentor.image,
	asProfessor                              : true,
	status_id                                : scheduleRef[key].mentor.bool_enabled ? employeeStatus.DISABLED : scheduleRef[key].mentor.employee.status_id
})
																			  }
																			>
																				<FontAwesomeIcon icon={solid("ellipsis")} />
																			</button>
																		}</td>
																	</tr>
																	<tr>
																		<td style={{color : "var(--wa-apagado)", height : "38px"}}>
																			<FontAwesomeIcon icon={solid("users")} />
																		</td>
																		<td style={{width : "100%"}}>
																			<span style={{color : "var(--wa-apagado)"}}>
																				Capacidad:
																				&nbsp;
																			</span>	
																			<CapacityComponent currentCapacity={+scheduleRef[key].enrolled_students}
																			  maxCapacity={+classroomRef["C_" + scheduleRef[key].classroom_id].students_limit} 
																			/>
																		</td>
																	</tr>
																	<tr>
																		<td style={{color : "var(--wa-apagado)", height : "38px"}}>
																			<FontAwesomeIcon icon={solid("address-book")} />
																		</td>
																		<td style={{width : "100%"}}>
																			<span style={{color : "var(--wa-apagado)"}}>
																				Ins/min:
																				&nbsp;
																			</span>
																			<EnrollmentsComponent minEnrollments={scheduleRef[key].students_minimum}
																			  currentEnrollments={scheduleRef[key].enrolled_students}
																			/>
																		</td>
																	</tr>
																	<tr>
																		<td style={{color : "var(--wa-apagado)", height : "38px"}}>
																			<FontAwesomeIcon icon={solid("list-check")} />
																		</td>
																		<td style={{width : "100%"}}>
																			<span style={{color : "var(--wa-apagado)"}}>
																				Asistencias:
																				&nbsp;
																			</span>
																			<AttendanceComponent idealAttendance={dayAttendanceRef[key][1]}
																			  currentAttendance={dayAttendanceRef[key][0]}
																			  listTaken={dayAttendanceRef[key][2]}
																			/>
																		</td>
																		{/*
																			(2026-10-22) Los botones se acomodan en renglones si la columna es angosta,
																			para no salirse de la tarjeta.
																			La flechita no decia a donde llevaba. Ahora son dos cosas
																			separadas: el chevron abre aqui mismo quien vino y quien no
																			(cerrado por omision), y "Asistencias" lleva al modulo, a ese
																			horario. (Esteban, 2026-09-21)
																		*/}
																		<td style={{width : "1px"}}>{
																			scheduleRef[key].enrollments.length < 1 ? "" : <span style={{display : "inline-flex", flexWrap : "wrap", justifyContent : "flex-end", gap : "4px", alignItems : "center"}}>
																				<button type="button" className="btn btn-default"
																				  disabled={idleLocked}
																				  aria-label={scheduleRef[key].attendanceShown ? "Ocultar asistencias" : "Ver asistencias"}
																				  onClick=
																				  {
																					()=> setScheduleRef
																					({
																						...scheduleRef, [key] : {...scheduleRef[key],
																						attendanceShown : !scheduleRef[key].attendanceShown}
																					})
																				  }
																				>
																					<FontAwesomeIcon icon={scheduleRef[key].attendanceShown ? solid("chevron-up") : solid("chevron-down")} />
																				</button>
																				<button type="button" className="btn btn-default"
																				  disabled={idleLocked}
																				  onClick={() => navegar("/attendances?horario=" + scheduleRef[key].id)}
																				>
																					<span className="waVer">Asistencias</span>
																				</button>
																			</span>
																		}</td>
																	</tr>
{
	!scheduleRef[key].attendanceShown ? "" : <tr>
		<td />
		<td colSpan={2}><div style={{maxHeight : "260px", overflowY : "auto"}}>
			<ul className="calDiaAsistencias">{
				scheduleRef[key].students
					.filter((student : any) => student.enrollments[0].status_id != ENROLLMENT_STATUS.NOT_STARTED)
					.map
					(
						(student : any)=>
						{
							const marca = scheduleRef[key].attendanceRef["S_" + student.id]
							const vino  = marca === 1

							return <li key={"as_" + student.id} className={marca == null ? "sinMarcar" : (vino ? "vino" : "falto")}>
								<span className="calDiaAsNombre">
									{nombreDeAlumno(student.first_name, student.last_name)}
								</span>
								<span className="calDiaAsMarca">
									{marca == null ? "Sin marcar" : (vino ? "Presente" : "Ausente")}
								</span>
							</li>
						}
					)
			}</ul>
		</div></td>
	</tr>
}
																	<tr>
																		<td style={{color : "var(--wa-apagado)", height : "38px"}}>
																			<FontAwesomeIcon icon={solid("money-bills")} />
																		</td>
																		<td style={{width : "100%"}}>
																			<span style={{color : "var(--wa-apagado)"}}>
																				Saldos pendientes:
																				&nbsp;
																			</span>
																			<PendingBalanceComponent
																			  pendingBalancesCount={scheduleRef[key].tuitionIndebtedCount}
																			/>
																		</td>
																		<td style={{width : "1px"}}>{
																			scheduleRef[key].tuitionIndebtedCount < 1 ? "" : <button
																			  className="btn btn-default" disabled={idleLocked}
																			  type="button" onClick=
																			  {
																				()=>
																				{
																					setScheduleRef
																					({
																						...scheduleRef, [key] : {...scheduleRef[key],
																						tuitionIndebtedShown  : !scheduleRef[key].tuitionIndebtedShown}
																					})
																				}
																			  }
																			>
																				<FontAwesomeIcon icon={scheduleRef[key].tuitionIndebtedShown ? solid("chevron-down") : solid("chevron-up")} />
																			</button>
																		}</td>
																	</tr>
{
	!scheduleRef[key].tuitionIndebtedShown || scheduleRef[key].tuitionIndebtedCount < 1 ? "" : <tr>
		<td />
		<td colSpan={2}><div style={{maxHeight : "260px", overflowY : "auto"}}>
			<table style={{width : "100%", fontWeight : 300, borderCollapse : "separate", borderSpacing : "0px 2px"}}>
				<tbody>{
					scheduleRef[key]
						.students
						.filter
						(
							(student : any)=>
							(
								((+student.enrollments[0].expiredMonthlyEnrollmentBalance) > 0)
									&&
								student.enrollments[0].status_id != ENROLLMENT_STATUS.NOT_STARTED
							)
						)
						.sort((a : any, b : any)=> b.enrollments[0].expiredMonthlyEnrollmentBalance - a.enrollments[0].expiredMonthlyEnrollmentBalance)
						.map
						(
							(student : any) => <tr
							  style={{color : "#FFFFFF"}}
							  key={"t" + student.id}
							>
								<td style={{paddingLeft : "5px", background : "var(--wa-rojo-lleno)", borderRadius : "15px 0px 0px 15px", width : "24px"}}>{
									/*
										El aviso es para quien debe Y ADEMAS vino hoy: es el momento de
										cobrarle. Antes se leia status_id, que no existe, asi que el aviso
										no salia nunca. (Esteban, 2026-09-21)
									*/
									scheduleRef[key].attendanceRef["S_" + student.id] !== 1 ? "" : <FontAwesomeIcon
									  icon={solid("circle-exclamation")}
									  title="Vino hoy"
									/>
								}</td>
								{/*
									El nombre lleva al estado de cuenta de esa persona: se veia quien debe
									pero habia que ir a buscarlo a Finanzas a mano. (Esteban, 2026-09-21)
								*/}
								<td style={{background : "var(--wa-rojo-lleno)"}}>
									<button
									  type="button"
									  className="calDiaDeudor"
									  disabled={idleLocked}
									  title="Ver su estado de cuenta"
									  onClick=
									  {
										() => navegar("/statement?alumno=" + student.id + "&plantel=" + campusRef[currentUserCampus].id)
									  }
									>
										{nombreDeAlumno(student.first_name, student.last_name)}
									</button>
								</td>
								<td style={{width : "1px", paddingRight : "5px", background : "var(--wa-rojo-lleno)", borderRadius : "0px 15px 15px 0px", textAlign : "right"}}>
									<div style={{display : "flex", alignItems : "stretch"}}>
										<span style={{paddingRight : "5px"}}>
											{currencySymbol}</span>
										<span style={{flexGrow : 1, whiteSpace : "nowrap"}}>{
											sprintf("%.02f", student.enrollments[0].expiredMonthlyEnrollmentBalance)
										}</span>
									</div>
								</td>
							</tr>
						)
				}</tbody>
			</table>
		</div></td>
	</tr>
}
																</tbody></table>
																<div style={{height : "calc(100% - 160px)"}} />
																<div style={{padding : "22px 0px 14px"}}>
																	<table style={{width : "100%"}}>
																		<tbody>
																			<tr>
																				<td style={{width : "100%"}}>
																					<div className="calDiaChips">
																						<ChipAula tono="edificio" texto={buildingRef["B_" + classroomRef["C_" + scheduleRef[key].classroom_id].building_id].name} />
																						<ChipAula tono="aula" texto={classroomRef["C_" + scheduleRef[key].classroom_id].name} />
																					</div>
																				</td>
																				<td style={{paddingLeft : "10px"}}>
																					<button type="button" className="btn btn-default"
																					  disabled={idleLocked} onClick=
																					  {
																						()=>
																						{
																							let hours : any = {}

																							scheduleRef[key].sessions.map
																							(
																								(session : any)=>
																								{
																									let currentHours : number = session.start_hours

																									while(currentHours < session.end_hours)
																									{
																										hours[sprintf("DH%d%03d", session.week_day, currentHours)] = true
																										currentHours += 5
																									}
																								}
																							)

																							promptEnrolling
																							(
																								courseRef["C_" + levelRef["L_" + scheduleRef[key].level_id].course_id],
																								{...levelRef["L_" + scheduleRef[key].level_id]},
																								{
																									...scheduleRef[key], hours : hours,
																									period : periodRef["P_" + scheduleRef[key].period_id],
																									classroom :
																									{
																										...classroomRef["C_" + scheduleRef[key].classroom_id],
																										building : buildingRef["B_" + classroomRef["C_" + scheduleRef[key].classroom_id].building_id],
																									}
																								}
																							)
																						}
																					  }
																					>
																						<span className="waVer">Ver</span>
																					</button>
																				</td>
																			</tr>
																		</tbody>
																	</table>
																</div>
															</div>
														</div>
															:
														(
															!availabilityDisplay ? "" : <div
															  style={{border : "1px solid var(--wa-acento)", borderRadius : "5px",flex : 1}}
															>
																<div style={{padding : "15px", background : "var(--wa-libre)", border : "10px dashed var(--wa-libre-filo)", height : "100%"}}>
																	<table style={{fontWeight : "900", width : "100%"}}><tbody>
																		<tr>
																			<td style={{verticalAlign : "top", paddingRight : "10px"}}>
																				<CustomIcon name="books2" style={{fill : "var(--wa-acento)"}} />
																			</td>
																			<td style={{width : "100%"}}>
																				Espacio Libre
																			</td>
																		</tr>
																		<tr>
																			<td style={{color : "var(--wa-apagado)", height : "38px"}}>
																				<FontAwesomeIcon icon={regular("clock")} />
																			</td>
																			<td style={{width : "100%"}}>
																				<span style={{color : "var(--wa-apagado)"}}>
																					Horario:
																					&nbsp;
																				</span>
																				{
																					sprintf
																					(
																						"%02d:%02d-%02d:%02d",
																						(
																							scheduleRef["A_" + key].start_hours-
																								(scheduleRef["A_" + key].start_hours % 10)
																						) / 10,
																						(scheduleRef["A_" + key].start_hours % 10) * 6,
																						(
																							scheduleRef["A_" + key].end_hours-
																								(scheduleRef["A_" + key].end_hours % 10)
																						) / 10,
																						(scheduleRef["A_" + key].end_hours % 10) * 6
																					)
																				}
																			</td>
																		</tr>
																		<tr>
																			<td style={{color : "var(--wa-apagado)", height : "38px"}}>
																				<FontAwesomeIcon icon={solid("users")} />
																			</td>
																			<td style={{width : "100%"}}>
																				<span style={{color : "var(--wa-apagado)"}}>
																					Cupo:
																					&nbsp;
																				</span>
																				{scheduleRef["A_" + key].classroom.students_limit}
																			</td>
																		</tr>
																	</tbody></table>
																	<div style={{height : "calc(100% - 160px)"}} />
																	<div className="calDiaChips" style={{padding : "22px 0px 14px"}}>
																		<ChipAula tono="edificio" texto={buildingRef["B_" + scheduleRef["A_" + key].classroom.building_id].name} />
																		<ChipAula tono="aula" texto={scheduleRef["A_" + key].classroom.name} />
																	</div>
																</div>
															</div>
														)
													}</div>
												</div>
											)
										}</div>
									</td>
								</React.Fragment>
							)
						}</tr>
				}
			</tbody></table></div>
		</div>
		{
			selectedEnrollment == null || selectedUser ? "" : <EnrollmentPrompt closeFn={() => {setSelectedEnrollment(null)}}
			  afterCloseFn={search} selectedEnrollment={selectedEnrollment} profileViewingFn={setSelectedUser}
			/>
		}
		{
			selectedUser == null ? "" : <UserPrompt promptCloseFn={() => setSelectedUser(null)} selectedUser={selectedUser}
			  employeeView={selectedUser.employeeView}
			/>
		}
		{
			selectedClassroom == null ? "" : <ClassroomPrompt
			  closeFn={() => setSelectedClassroom(null)}
			  selectedClassroom={selectedClassroom}
			/>
		}
		{
			selectedSchedule == null ? "" : <DataDisplayModal headIcon={<FontAwesomeIcon icon={solid("list-check")} />}
			  promptCloseFn={() => setSelectedSchedule(null)} entityName={"Asistencias"} editableRecord={null} unrequiredEmphasis
			>
				<div className="card card-default" style={{paddingTop : "unset"}}>
					<div style={{background : selectedSchedule.color, width : "100%", height : "30px", borderRadius : "0px 0px 15px 15px"}} />
					<div style={{paddingTop : "15px"}}><div style={{display : "flex", flexWrap : "wrap"}}>
						<div style={{borderBottom : "1px solid var(--wa-acento)", flex : 1}}><table style={{width : "100%"}}>
							<tbody>	
								<tr><td><div style={{display : "flex", alignItems : "stretch", flexWrap : "wrap"}}>
									<div style={{flex : 1}}><h5 style={{fontWeight : "unset", color : "var(--wa-apagado)"}}>
										{cursoCorto(selectedSchedule.level.course.name)}
									</h5></div>
									<div>
										<div style={{display : "inline-flex"}}>
											<label style={{display : "inline-block", position : "relative", color : "var(--wa-apagado)", padding : "0px 15px"}}>
												Horario
											</label>
											{horarioBonito(selectedSchedule.name)}
										</div>
										<div style={{display : "inline-flex"}}>
											<label style={{display : "inline-block", position : "relative", color : "var(--wa-apagado)", padding : "0px 15px"}}>
												Aula
											</label>
											{selectedSchedule.classroom.name}
										</div>
									</div>
								</div></td></tr>
							</tbody>
						</table></div>
					</div></div>
					{/*
						La matriz de alumnos por fechas no cabe en un telefono: pedia
						arrastrar de lado para leer un nombre. En telefono se muestra lo
						que se viene a ver aqui —quien es y como va— y el detalle dia por
						dia se consulta en el Historico. (Esteban, 2026-09-16)
					*/}
					{
						!esTelefono ? null : <div className="waLt">{
							selectedSchedule.enrollments.map
							(
								(enrollment : any) =>
								{
									const asistio = attendanceCountRef["S_" + enrollment.student.id] || 0
									const porcentaje = dateCount < 1 ? 0 : ~~((asistio / dateCount) * 100)

									return <article className="waLtTarjeta" key={"MT_" + enrollment.student.id}>
										<header className="waLtCabecera">
											<span className="waLtTitulo">
												{nombreDeAlumno(enrollment.student.first_name, enrollment.student.last_name)}
											</span>
											<span className={"waLtChip " + (porcentaje >= 75 ? "verde" : "ambar")}>
												{porcentaje}%
											</span>
										</header>
										<dl className="waLtCampos">
											<div className="waLtCampo">
												<dt>Asistencias</dt>
												<dd>{asistio} de {dateCount}</dd>
											</div>
											<div className="waLtCampo">
												<dt>Matrícula</dt>
												<dd>{enrollment.student.id}</dd>
											</div>
										</dl>
									</article>
								}
							)
						}</div>
					}
					<div style={{overflow : "auto", display : esTelefono ? "none" : "block"}}>
						<table className="specialTable">
							<thead>
								<tr>
									<td colSpan={4} className="text-center">
										Alumno
									</td>
									{
										monthRef.map
										(
											(month : string)=>
											{
												if(monthDateCount[month])
												{
													return <td colSpan={monthDateCount[month]} key={month}>
														{month}
													</td>
												}
											}
										)
									}
									<td colSpan={2} className="text-center">
										Asistencia
									</td>
								</tr>
								<tr>
									<th />
									<th style={{width : "1px"}}>
										Matrícula
									</th>
									<th>
										Nombres
									</th>
									<th>
										Apellidos
									</th>
									{
										dateList.map
										(
											(date : string) => <th key={date} style={{width : "1px"}}>
												{dateRef[date]}
											</th>
										)
									}
									<th style={{width : "1px"}}>
										Total
									</th>
									<th style={{width : "1px", textAlign : "center"}}>
										%
									</th>
								</tr>
							</thead>
							<tbody>{
								selectedSchedule.enrollments.map
								(
									(enrollment : any) => <tr key={"S_" + enrollment.student.id}>
										<td />
										<td style={{borderBottom : "1px solid var(--wa-gris)"}}>
											{enrollment.student.id}
										</td>
										<td>
											{enrollment.student.first_name}
										</td>
										<td>
											{enrollment.student.last_name}
										</td>
										{
											dateList.map
											(
												(date : string) => <td key={date} className="text-center"
												  style={{color : attendanceStudentRef[date + "S_" + enrollment.student.id] == attendanceStatus.ABSENT ? "var(--wa-oro)" : "var(--wa-acento)"}}
												>{
													<FontAwesomeIcon  icon=
													  {
														attendanceStudentRef[date + "S_" + enrollment.student.id] == null
																?
															regular("square")
																:
															(
																attendanceStudentRef[date + "S_" + enrollment.student.id] == attendanceStatus.COMPENSATION
																		?
																	solid("bandage")
																		:
																	(
																		attendanceStudentRef[date + "S_" + enrollment.student.id] == attendanceStatus.ABSENT
																				?
																			regular("square-minus")
																				:
																			regular("square-check")
																	)
															)
													  }
													/>
												}</td>
											)
										}
										<td className="text-end"><div style={{display : "flex"}}>
											<div style={{flex : 1}}>
												{attendanceCountRef["S_" + enrollment.student.id]}
											</div>
											<div style={{padding : "0px 5px"}}>
												/
											</div>
											<div>
												{dateCount}
											</div>
										</div></td>
										<td className="text-end" style={{textAlign : "right"}}
										  ref={el => {if(el){el.style.setProperty("border-bottom", "1px solid var(--wa-gris)", "important")}}}
										><div style={{display : "flex"}}>
											<div style={{flex : 1}}>{
												~~(
													(
														attendanceCountRef["S_" + enrollment.student.id]
															/ dateCount
													)
													* 100
												)
											}</div>
											<div style={{paddingLeft : "5px"}}>
												%
											</div>
										</div></td>
									</tr>
								)
							}</tbody>
						</table>
					</div>
				</div>
			</DataDisplayModal>
		}
	</>
}

export default DailyView
